/**
 * Streaming handlers — the out-of-band lane.
 *
 * Five events that arrive alongside the token stream rather than inside it:
 * session lifecycle (SESSION_COMPACTED / SESSION_STATE_CHANGED), per-turn
 * credits (MESSAGE_CREDIT_UPDATE), and the AGENT_CONTEXT_USAGE /
 * AGENT_MEMORY_RECALL telemetry.
 *
 * None of them touches the rAF delta buffer or the per-turn error-dedup set in
 * `streamingHandlers.ts` — that is why they are the region that moved.
 *
 * Registered by `registerStreamingHandlers`, which owns the teardown this
 * returns. `streamingHandlers.registrationTable.test.ts` pins both halves.
 */

import type { Socket } from "socket.io-client";
import type { MemoryRecallEntry } from "@bash/shared";
import type { ClientPayloadOf } from "../socketService";
import { EVENTS } from "@/constants/events";
import { useTaskStore } from "../taskStore";
import { DEFAULT_AGENT } from "../types";
import { findMessageByAnyId } from "../helpers/findMessage";

const taskStore = useTaskStore;

export function registerStreamingSessionHandlers(socket: Socket): () => void {
  // SESSION_COMPACTED — SDK auto-summarized older turns to free context.
  // Appends an inline CompactionBlock to the task timeline so the agent's
  // apparent memory loss is legible instead of mysterious.
  const handleSessionCompacted = (
    data: ClientPayloadOf<typeof EVENTS.SESSION_COMPACTED>
  ) => {
    const taskId = data?.taskId;
    if (!taskId) return;

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;

      // Dedupe: if the last message already ends with a compaction block,
      // skip — some event-pipeline paths deliver the same event twice.
      const lastMsg = task.messages[task.messages.length - 1];
      const lastBlock = lastMsg?.content?.[lastMsg.content.length - 1];
      if (lastBlock && (lastBlock as { type: string }).type === "compaction")
        return;

      const compactionBlock = {
        type: "compaction" as const,
        trigger:
          data.trigger === "manual" ? ("manual" as const) : ("auto" as const),
        summary: typeof data.summary === "string" ? data.summary : "",
      };

      // Emit as a standalone assistant message so it lives between turns
      // in the timeline rather than being attached to a streaming message.
      const compactionClientId = `compaction-${Date.now()}`;
      task.messages = [
        ...task.messages,
        {
          id: compactionClientId,
          role: "assistant" as const,
          content: [compactionBlock],
          timestamp: new Date(),
          agentSlug: task.agentSlug || DEFAULT_AGENT,
          identity: { kind: "optimistic", clientId: compactionClientId },
        },
      ];
    });
  };

  // MESSAGE_CREDIT_UPDATE — per-turn credit cost landed (post AGENT_RESULT).
  // Merge creditCost into the identified message's turn_footer block so the
  // MessageFooter's credits chip flips from hidden to visible within ~1s of
  // turn end, no reload needed.
  const handleMessageCreditUpdate = (
    data: ClientPayloadOf<typeof EVENTS.MESSAGE_CREDIT_UPDATE>
  ) => {
    const taskId = data?.taskId;
    const messageId = data?.messageId;
    const creditCost = data?.creditCost;
    if (!taskId || !messageId || typeof creditCost !== "number") return;

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) {
        // [Credit] observability — task missing from store is unexpected;
        // diagnose ID drift between server in-memory and frontend store.
        // eslint-disable-next-line no-console
        console.warn(
          `[Credit] MESSAGE_CREDIT_UPDATE for unknown task=${taskId}`
        );
        return;
      }
      // Identity-aware lookup: matches on client / server / sdk uuid in
      // one pass. AGENT_END reconciliation stamped a persisted identity
      // onto the streaming message, so the server's messageId lands
      // exactly once here — no last-assistant guessing.
      const msgIdx = findMessageByAnyId(task.messages, messageId);
      // RENDER_FROM_TRUTH — the event arrives ~1s after AGENT_END, when the
      // row may still be sitting in the HELD overlay (its sync round-trip
      // pending). Stamp the overlay's footer; settleOverlay's carry doesn't
      // cover footers, but the DB has the cost persisted, so the adopted row
      // carries it — this write keeps the chip live in the interim.
      const heldOverlay =
        msgIdx === -1 &&
        task.streamingMessage &&
        (task.streamingMessage.expectedServerId === messageId ||
          (task.streamingMessage.identity?.kind === "persisted" &&
            task.streamingMessage.identity.serverId === messageId))
          ? task.streamingMessage
          : null;
      if (msgIdx === -1 && !heldOverlay) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Credit] MESSAGE_CREDIT_UPDATE message=${messageId} not found in task=${taskId} (msg count=${task.messages.length})`
        );
        return;
      }
      const msg = heldOverlay ?? task.messages[msgIdx];
      const footerIdx = msg.content.findIndex((b) => b.type === "turn_footer");
      if (footerIdx === -1) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Credit] MESSAGE_CREDIT_UPDATE message=${messageId} has no turn_footer block`
        );
        return;
      }
      (
        msg.content[footerIdx] as unknown as { creditCost?: number }
      ).creditCost = creditCost;
    });
  };

  // AGENT_CONTEXT_USAGE — per-turn context window snapshot for ContextMeter
  const handleAgentContextUsage = (data: Record<string, unknown>) => {
    const taskId = data.taskId as string;
    if (!taskId) return;
    const usage = data.usage as Record<string, unknown> | undefined;
    if (!usage) return;
    if (
      typeof usage.totalTokens !== "number" ||
      typeof usage.maxTokens !== "number"
    )
      return;
    useTaskStore.setState((state) => {
      const t = state.tasks.get(taskId);
      if (!t) return;
      t.contextUsage = {
        totalTokens: usage.totalTokens as number,
        maxTokens: usage.maxTokens as number,
        percentage: typeof usage.percentage === "number" ? usage.percentage : 0,
        model: typeof usage.model === "string" ? usage.model : "",
      };
    });
  };

  // AGENT_MEMORY_RECALL — SDK surfaces memory files this turn
  const handleAgentMemoryRecall = (data: Record<string, unknown>) => {
    const taskId = data.taskId as string;
    if (!taskId) return;
    const mode = data.mode as "select" | "synthesize" | undefined;
    const memories = data.memories as unknown;
    if (!mode || (mode !== "select" && mode !== "synthesize")) return;
    if (!Array.isArray(memories)) return;
    useTaskStore.setState((state) => {
      const t = state.tasks.get(taskId);
      if (!t) return;
      const existing = t.memoryRecalls ?? [];
      t.memoryRecalls = [
        ...existing,
        {
          mode,
          memories: memories as MemoryRecallEntry[],
          receivedAt: Date.now(),
        },
      ];
    });
  };

  // K3-T3 / P3.6: SESSION_STATE_CHANGED — authoritative turn-phase signal.
  // Drives phase independently of AGENT_START/AGENT_END. AGENT_END remains the
  // finalization carrier; this event flips the `phase` flag for ALL arms.
  //
  // State → phase mapping:
  //   running        → 'processing'  (turn started)
  //   idle           → 'idle'        (turn completed, no pending interaction)
  //   requires_action → 'waiting'    (paused for human input)
  //
  // Authority status (P3.6 ratchet 1):
  //   • running→processing: SOLE authority. AGENT_START no longer writes phase —
  //     every sandbox AGENT_START is followed by SESSION_STATE_CHANGED{running} on
  //     the same socket, and the server never originates AGENT_START.
  //   • idle→idle: CO-owner. AGENT_END still writes idle for the five AGENT_END
  //     paths that emit no paired SESSION_STATE_CHANGED{idle} (see handleAgentEnd).
  const handleSessionStateChanged = (data: Record<string, unknown>) => {
    const taskId =
      (data.taskId as string | undefined) ||
      (data.sessionId as string | undefined);
    if (!taskId || !data.state) return;

    const stateToPhase: Record<string, "processing" | "idle" | "waiting"> = {
      running: "processing",
      idle: "idle",
      requires_action: "waiting",
    };
    const phase = stateToPhase[data.state as string];
    if (!phase) return; // unknown state — no-op, no throw

    useTaskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;
      task.phase = phase;
      // The runner's own first terminal word seals the turn — it arrives a beat
      // BEFORE the AGENT_END that carries the finalization (K3-T3 ordering), so
      // sealing here too closes the gap in which the turn read idle with no end
      // time yet (the done clock blinked off for ~100ms at every turn end).
      if (phase === "idle") task.turnSealedAt = Date.now();
    });
  };

  socket.on(EVENTS.AGENT_CONTEXT_USAGE, handleAgentContextUsage);
  socket.on(EVENTS.AGENT_MEMORY_RECALL, handleAgentMemoryRecall);
  socket.on(EVENTS.SESSION_COMPACTED, handleSessionCompacted);
  socket.on(EVENTS.MESSAGE_CREDIT_UPDATE, handleMessageCreditUpdate);
  socket.on(EVENTS.SESSION_STATE_CHANGED, handleSessionStateChanged);

  return () => {
    socket.off(EVENTS.AGENT_CONTEXT_USAGE, handleAgentContextUsage);
    socket.off(EVENTS.AGENT_MEMORY_RECALL, handleAgentMemoryRecall);
    socket.off(EVENTS.SESSION_COMPACTED, handleSessionCompacted);
    socket.off(EVENTS.MESSAGE_CREDIT_UPDATE, handleMessageCreditUpdate);
    socket.off(EVENTS.SESSION_STATE_CHANGED, handleSessionStateChanged);
  };
}
